// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel · B2B bulk orders  (API + labels for pages/admin/AdminBulkOrders)
// Typed wrapper over the staff bulk-order endpoints (GET/POST /admin/bulk-orders, /admin/bulk-orders/{ref}/…,
// GET /admin/bulk-orders/summary). These are STAFF records of a phone/WhatsApp/email/walk-in order from a
// restaurant, hotel or party — never a customer checkout, a payment or a GST e-invoice; `paymentStatus` and
// `paymentTerms` are only what staff typed. Reads work for any staff role; writes need admin/editor (the
// backend enforces it, the UI only hides what a role can't do). Driver assignment reuses the real fleet
// directory (`utils/fleetApi`).
// ─────────────────────────────────────────────────────────────────────────────
import { apiAuthed } from "./api";

export type BulkOrderStatus = "requested" | "confirmed" | "preparing" | "dispatched" | "delivered" | "cancelled";
export type Channel = "phone" | "whatsapp" | "email" | "walk_in" | "other";
export type PaymentStatus = "unpaid" | "paid";

export const STATUS_LABEL: Record<BulkOrderStatus, string> = {
  requested: "Requested",
  confirmed: "Confirmed",
  preparing: "Preparing",
  dispatched: "Dispatched",
  delivered: "Delivered",
  cancelled: "Cancelled",
};
/** Forward order of the real flow (mirrors `backend/app/domain/bulk_order.py::FLOW`); cancel is reachable from any non-final step. */
export const STATUS_FLOW: BulkOrderStatus[] = ["requested", "confirmed", "preparing", "dispatched", "delivered"];
export const CHANNEL_LABEL: Record<Channel, string> = { phone: "Phone", whatsapp: "WhatsApp", email: "Email", walk_in: "Walk-in", other: "Other" };
export const CHANNELS = Object.keys(CHANNEL_LABEL) as Channel[];
/** Status chip colours (mirrors `components/admin/adminStyles.ts::STATUS_PILL`'s palette for the retail-order console). */
export const BULK_STATUS_PILL: Record<BulkOrderStatus, string> = {
  requested: "bg-coral-500/15 text-coral-700 dark:text-coral-300",
  confirmed: "bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-100",
  preparing: "bg-ocean-700/15 text-ocean-900 dark:bg-white/15 dark:text-foam-100",
  dispatched: "bg-ocean-800 text-white",
  delivered: "bg-teal-500/15 text-teal-800 dark:text-teal-300",
  cancelled: "bg-ocean-900/10 text-ocean-800/80 line-through dark:bg-white/10 dark:text-foam-100/70",
};

export interface BulkLine {
  description: string;
  catchId: string | null;
  weightKg: number | null;
  amountInr: number;
}
export interface BulkDelivery {
  addressLine: string;
  city: string;
  pincode: string;
  notes: string;
}
export interface StatusChange {
  status: BulkOrderStatus;
  at: string;
}
export interface BulkOrder {
  ref: string;
  accountName: string;
  contactName: string | null;
  phone: string;
  email: string | null;
  gstin: string | null;
  poReference: string | null;
  channel: Channel;
  items: BulkLine[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  totalWeightKg: number;
  delivery: BulkDelivery;
  requestedFor: string | null;
  paymentTerms: string;
  paymentStatus: PaymentStatus;
  status: BulkOrderStatus;
  active: boolean;
  history: StatusChange[];
  driverId: string | null;
  driverName: string | null;
  driverAssignedAt: string | null;
  opsNote: string | null;
  opsNoteBy: string | null;
  opsNoteAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface BulkOrderSummary {
  totalOrders: number;
  activeOrders: number;
  awaitingPrep: number;
  weightKgActive: number;
  outstandingInr: number;
}
export interface BulkLineInput {
  description: string;
  catchId?: string;
  weightKg?: number;
  amountInr: number;
}
export interface CreateBulkOrderInput {
  accountName: string;
  contactName?: string;
  phone: string;
  email?: string;
  gstin?: string;
  poReference?: string;
  channel: Channel;
  items: BulkLineInput[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  delivery: { addressLine: string; city: string; pincode: string; notes?: string };
  requestedFor?: string;
  paymentTerms: string;
}

const base = (ref: string) => `/admin/bulk-orders/${encodeURIComponent(ref)}`;
export const fetchBulkOrderSummary = () => apiAuthed<BulkOrderSummary>("GET", "/admin/bulk-orders/summary");
export const fetchBulkOrders = (status?: BulkOrderStatus) =>
  apiAuthed<BulkOrder[]>("GET", status ? `/admin/bulk-orders?status=${status}` : "/admin/bulk-orders");
export const fetchBulkOrder = (ref: string) => apiAuthed<BulkOrder>("GET", base(ref));
export const createBulkOrder = (input: CreateBulkOrderInput) => apiAuthed<BulkOrder>("POST", "/admin/bulk-orders", input);
export const setBulkOrderStatus = (ref: string, status: BulkOrderStatus) => apiAuthed<BulkOrder>("PUT", `${base(ref)}/status`, { status });
export const setBulkOrderDriver = (ref: string, driverId: string | null) => apiAuthed<BulkOrder>("PUT", `${base(ref)}/driver`, { driverId });
export const setBulkOrderPayment = (ref: string, paymentStatus: PaymentStatus) => apiAuthed<BulkOrder>("PUT", `${base(ref)}/payment`, { paymentStatus });
export const saveBulkOrderNote = (ref: string, note: string) => apiAuthed<BulkOrder>("PUT", `${base(ref)}/note`, { note });

/** The forward status this order can move to next, or null once it's final (mirrors the backend's FLOW check). */
export function nextStatus(status: BulkOrderStatus): BulkOrderStatus | null {
  const index = STATUS_FLOW.indexOf(status);
  return index === -1 || index === STATUS_FLOW.length - 1 ? null : STATUS_FLOW[index + 1];
}
export const canWriteBulkOrders = (role: string | null | undefined) => role === "admin" || role === "editor";
export const inr = (paise: number) => `₹${paise.toLocaleString("en-IN")}`;
